import React, { createRef, FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useResolvedPath } from 'react-router-dom';
import styled from 'styled-components';
import Box from './Box';
import RotatingChevron from './RotatingChevron';

const Container = styled.li`
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  width: 100%;
  margin-bottom: 0.5rem;
  color: var(--gray-700);
  stroke: var(--gray-700);
`;

const useRefHeight = (ref: React.RefObject<HTMLElement>) => {
  const [height, setHeight] = useState<number | 'auto'>('auto');
  useEffect(() => {
    if (ref.current) {
      const { current } = ref;
      const boundingRect = current.getBoundingClientRect();
      const { height } = boundingRect;
      setHeight(Math.round(height));
    }
  }, [ref]);
  return height;
};

type ContextType = {
  open: boolean;
};
export const LinkDropDownContext = React.createContext({} as ContextType);
const { Provider } = LinkDropDownContext;

type LinkDropDownProps = {
  children: React.ReactNode;
  icon: React.ReactElement;
  title: string;
  href: string;
  isCollapsed?: boolean;
};
const LinkDropDown: FC<LinkDropDownProps> = ({
  icon,
  title,
  href,
  children,
  isCollapsed,
}) => {
  const [open, setOpen] = useState(false);

  // Empêche l'ouverture si le panneau est réduit
  const switchOpen = () => {
    if (!isCollapsed) {
      setOpen(!open);
    }
  };

  const secondaryList = createRef<HTMLUListElement>();
  const location = useLocation();
  const path = useResolvedPath(href);

  const isActive = useMemo(() => {
    let locationPathname = location.pathname.toLowerCase();
    let toPathname = path.pathname.toLowerCase();
    return (
      locationPathname === toPathname ||
      (locationPathname.startsWith(toPathname) &&
        locationPathname.charAt(toPathname.length) === '/')
    );
  }, [location, path]);

  // Si le menu est actif, on l'ouvre automatiquement, sauf s'il est réduit
  useEffect(() => {
    if (isActive && !isCollapsed) {
      setOpen(true);
    }
  }, [isActive, isCollapsed]);

  const height = useRefHeight(secondaryList);

  const value = useMemo(
    () => ({
      // En mode réduit, on force le contexte sur "fermé" pour les enfants
      open: isCollapsed ? false : open,
    }),
    [open, isCollapsed],
  );

  return (
    <Provider value={value}>
      <Container>
        <Box
          active={isActive}
          onClick={switchOpen}
          tabIndex={0}
          style={{ 
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            cursor: isCollapsed ? 'default' : 'pointer' 
          }}
          onKeyPress={(e) => {
            if (e.code === 'Enter') {
              switchOpen();
            }
          }}
        >
          {icon}
          {/* On masque le texte si réduit */}
          {!isCollapsed && <span style={{ marginLeft: '1rem' }}>{title}</span>}
          
          {/* On masque le chevron si réduit */}
          {!isCollapsed && <RotatingChevron active={isActive} open={open} />}
        </Box>

        {/* On masque totalement la liste secondaire si réduit */}
        {!isCollapsed && (
          <div
            style={{
              overflow: 'hidden',
              transition: '.2s',
              height: open ? height : 0,
            }}
          >
            <ul ref={secondaryList}>{children}</ul>
          </div>
        )}
      </Container>
    </Provider>
  );
};

export default LinkDropDown;
